import React, { useEffect, useState } from 'react';
import { getToken } from '../../utils/auth';
import ApiService from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

interface User {
  id: string;
  name: string;
  email: string;
  image?: string;
}

interface AddManagerModalProps {
  fundId: string;
  isOpen: boolean;
  onClose: () => void;
  onManagerAdded: () => void;
}

const AddManagerModal: React.FC<AddManagerModalProps> = ({
  fundId,
  isOpen,
  onClose,
  onManagerAdded,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setSelectedUserId('');
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        return;
      }

      const apiService = new ApiService(token);
      const response = await apiService.getUserList();

      if (response && Array.isArray(response)) {
        setUsers(response);
      } else {
        console.error('Invalid user list response:', response);
        showNotification('Failed to fetch users', 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Failed to fetch users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId) {
      showNotification('Please select a user', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        return;
      }

      const apiService = new ApiService(token);

      await apiService.addFundManager(fundId, selectedUserId);

      showNotification('Manager added successfully', 'success');
      onManagerAdded();
      onClose();
    } catch (error) {
      console.error('Error adding manager:', error);
      showNotification('Failed to add manager', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center border-b pb-3">
          <h3 className="text-xl font-semibold text-gray-900">
            Add Fund Manager
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          <div className="mb-4">
            <label
              htmlFor="user"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Select User
            </label>
            {loadingUsers ? (
              <div className="text-center py-2">Loading users...</div>
            ) : (
              <select
                id="user"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a user</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedUserId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Manager'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddManagerModal;
